//@ts-ignore
import { createSignal } from "solid-js";
import { createStore } from "solid-js/store";

// Existing state
export const [globalBackground, setGlobalBackground] = createSignal<string>("");
export const [globalStream, setGlobalStream] = createSignal<string>("");
export const [globalStreamType, setGlobalStreamType] = createSignal<string>("");
export const [isPlaying, setIsPlaying] = createSignal<boolean>(true);
export const [isLoading, setIsLoading] = createSignal<boolean>(false);
export const [playListUrl, setPlayListUrl] = createSignal<string>("");
export const [playListUsername, setPlayListUsername] = createSignal<string>("");
export const [playListPassword, setPlayListPassword] = createSignal<string>("");
export const [isVpnMode, setIsVpnMode] = createSignal<boolean>(false);
export const [isBuffering, setIsBuffering] = createSignal<boolean>(false);
export const [playListName, setPlayListName] = createSignal<string>("");
export const [hidePlaylistInfo, setHidePlaylistInfo] = createSignal<string>("");
export const [channelMenu, setChannelMenu] = createSignal(false);
export const [liveTvStreamID, setLiveTvStreamID] = createSignal<any>(null);
export const [lastStream, setLastStream] = createSignal<any>(null);
export const [showReconnecting, setShowReconnecting] = createSignal(false);
export const [deviceYear, setDeviceYear] = createSignal<any>(null);
export const [previewToNotShow, setPreviewToNotShow] = createSignal(true);

// New Device Information State
export const [deviceModel, setDeviceModel] = createSignal<string>("");
export const [tizenVersion, setTizenVersion] = createSignal<string>("");
export const [screenResolution, setScreenResolution] = createSignal<string>("");
export const [screenSize, setScreenSize] = createSignal<string>(""); // Physical size in inches
export const [deviceDUID, setDeviceDUID] = createSignal<string>("");
export const [deviceMacAddress, setDeviceMacAddress] = createSignal<string>("");
export const [networkType, setNetworkType] = createSignal<string>("");
export const [buildVersion, setBuildVersion] = createSignal<string>("");
export const [manufacturer, setManufacturer] = createSignal<string>("");
export const [samsungVersion, setSamsungVersion] = createSignal<string>("");
export const [storageInfo, setStorageInfo] = createSignal<string>("");

// Device capabilities
export const [deviceCapabilities, setDeviceCapabilities] = createStore({
  bluetoothSupported: false,
  wifiDirectSupported: false,
  usbHostSupported: false,
  webglSupported: false,
  generation: "", // Modern, Standard, Legacy
});

// Complete device info store
export const [deviceInfo, setDeviceInfo] = createStore({
  model: "",
  modelCode: "",
  year: 0,
  tizenVersion: "",
  buildVersion: "",
  manufacturer: "",
  screenResolution: "",
  screenSizeInches: "",
  physicalSize: "",
  duid: "",
  macAddress: "",
  networkType: "",
  samsungVersion: "",
  storage: {
    total: "",
    available: "",
  },
  capabilities: {
    bluetooth: false,
    wifiDirect: false,
    usbHost: false,
    webgl: false,
  },
  generation: "",
  lastUpdated: "",
});

export const [focusStore, setFocusStore] = createStore({
  //channels
  focusedIndexChannels: null,
  thumbnailIndexChannels: null,
  enterChannels: null,
  colonIndexChannels: null,
  //movies
  moviesIndex: null,
  moviesDetail: null,
  moviesID: null,
  moviesIDMap: null,
  //series
  seriesIndex: null,
  seriesDetail: null,
  seriesID: null,
});

// Helper function to update all device information from the collected data
export const updateDeviceInformation = (collectedDeviceData: any) => {
  console.log("### RAW DATA RECEIVED ###");
  console.log("typeof collectedDeviceData:", typeof collectedDeviceData);
  console.log("collectedDeviceData:", collectedDeviceData);
  console.log("collectedDeviceData.device_model:", collectedDeviceData.device_model);
  console.log("collectedDeviceData.device_year:", collectedDeviceData.device_year);
  console.log("########################");

  console.log("########", collectedDeviceData);

  // Update individual signals
  setDeviceModel(collectedDeviceData.device_model || "");
  setTizenVersion(collectedDeviceData.tizen_version || "");
  setScreenResolution(collectedDeviceData.screensize || "");
  setDeviceDUID(collectedDeviceData.samsung_duid || "");
  setDeviceMacAddress(collectedDeviceData.device_mac_address || "");
  setNetworkType(collectedDeviceData.network_type || "");
  setBuildVersion(collectedDeviceData.build_version || "");
  setManufacturer(collectedDeviceData.manufacturer || "Samsung");
  setSamsungVersion(collectedDeviceData.samsung_version || "");
  setDeviceYear(collectedDeviceData.device_year || 0);

  console.log("### AFTER SETTING ###");
  console.log("New deviceModel():", deviceModel());
  console.log("New deviceYear():", deviceYear());

  // Calculate screen size in inches
  const screenDiagonal = calculateScreenDiagonal(
    collectedDeviceData.physical_screen_width,
    collectedDeviceData.physical_screen_height,
  );
  setScreenSize(screenDiagonal);

  // Format storage info
  const storageText = formatStorage(collectedDeviceData.total_storage, collectedDeviceData.available_storage);
  setStorageInfo(storageText);

  // Update capabilities store
  setDeviceCapabilities({
    bluetoothSupported: collectedDeviceData.bluetooth_supported || false,
    wifiDirectSupported: collectedDeviceData.wifi_direct_supported || false,
    usbHostSupported: collectedDeviceData.usb_host_supported || false,
    webglSupported: !!collectedDeviceData.webgl_version,
    generation: determineDeviceGeneration(collectedDeviceData.device_year),
  });

  // Update complete device info store
  setDeviceInfo({
    model: collectedDeviceData.device_model || "",
    modelCode: collectedDeviceData.model_code || "",
    year: collectedDeviceData.device_year || 0,
    tizenVersion: collectedDeviceData.tizen_version || "",
    buildVersion: collectedDeviceData.build_version || "",
    manufacturer: collectedDeviceData.manufacturer || "Samsung",
    screenResolution: collectedDeviceData.screensize || "",
    screenSizeInches: screenDiagonal,
    physicalSize: formatPhysicalSize(
      collectedDeviceData.physical_screen_width,
      collectedDeviceData.physical_screen_height,
    ),
    duid: collectedDeviceData.samsung_duid || "",
    macAddress: collectedDeviceData.device_mac_address || "",
    networkType: collectedDeviceData.network_type || "",
    samsungVersion: collectedDeviceData.samsung_version || "",
    storage: {
      total: formatBytes(collectedDeviceData.total_storage),
      available: formatBytes(collectedDeviceData.available_storage),
    },
    capabilities: {
      bluetooth: collectedDeviceData.bluetooth_supported || false,
      wifiDirect: collectedDeviceData.wifi_direct_supported || false,
      usbHost: collectedDeviceData.usb_host_supported || false,
      webgl: !!collectedDeviceData.webgl_version,
    },
    generation: determineDeviceGeneration(collectedDeviceData.device_year),
    lastUpdated: new Date().toISOString(),
  });

  // Store in localStorage for persistence
  localStorage.setItem(
    "deviceInfo",
    JSON.stringify({
      model: collectedDeviceData.device_model,
      year: collectedDeviceData.device_year,
      tizenVersion: collectedDeviceData.tizen_version,
      screenSize: screenDiagonal,
      duid: collectedDeviceData.samsung_duid,
      generation: determineDeviceGeneration(collectedDeviceData.device_year),
      lastUpdated: new Date().toISOString(),
    }),
  );
};

// Helper functions
const calculateScreenDiagonal = (width: number, height: number): string => {
  if (!width || !height) return "";
  const diagonal = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2));
  const inches = diagonal / 25.4; // Convert mm to inches
  return `${Math.round(inches)}"`;
};

const formatPhysicalSize = (width: number, height: number): string => {
  if (!width || !height) return "";
  return `${Math.round(width)}×${Math.round(height)}mm`;
};

const formatStorage = (total: number, available: number): string => {
  const totalGB = Math.round(total / (1024 * 1024 * 1024));
  const availableGB = Math.round(available / (1024 * 1024 * 1024));
  return `${availableGB}GB / ${totalGB}GB available`;
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const determineDeviceGeneration = (year: number): string => {
  if (year >= 2020) return "Modern";
  if (year >= 2016) return "Standard";
  if (year > 0) return "Legacy";
  return "Unknown";
};

// Function to get device summary for display
export const getDeviceSummary = () => {
  const info = deviceInfo;
  return {
    displayName: `${info.manufacturer} ${info.model} (${info.year})`,
    screenInfo: `${info.screenSizeInches} ${info.screenResolution}`,
    systemInfo: `Tizen ${info.tizenVersion}`,
    generation: info.generation,
    storage: info.storage.available,
  };
};
